import { useCallback, useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion, type Variants } from 'framer-motion'
import { Tabs } from '@highlight/ui'
import { FadingScrollable } from '@renderer/chat'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { useSetFloatingBarScrollSlot } from '../../../atoms'
import { useViewBodyBottomChrome, useViewBodyBottomInset } from '../../../components/view'
import { useBodyScroll } from '../../../hooks/use-body-scroll'
import { useViewEntryScrollPersistence } from '../../../hooks/use-view-scroll-persistence'
import { hasMeetingTabs, isMeetingTab, listMeetingTabs, type MeetingViewLayout } from '../meeting-tabs'
import { useMeetingBriefBody } from '../use-meeting-brief-body'
import { useMeetingSummary } from '../use-meeting-summary'
import { useMeetingTab } from '../use-meeting-tab'
import { ScrollButton } from '../../../components/view-scroll-button'
import { MeetingViewBriefBody } from './brief-body'
import { useCanGenerateBrief } from './brief-generation'
import { MeetingViewBriefPanel } from './brief-panel'
import { FreeGraceHourNote } from './free-grace-hour-note'
import { HoursRanOutNote } from './hours-ran-out-note'
import { MeetingViewGetReady } from './get-ready'
import { useHoursPaywallVisible } from './meeting-hours-paywall'
import { MeetingViewRecapPanel } from './recap-panel'
import { MeetingViewTabBar } from './tab-bar'
import { MeetingViewUnderlinedTabBar } from './underlined-tab-bar'
import { MeetingViewTranscriptPanel } from './transcript-panel'
import { MeetingViewNotepadPanel } from './notepad-panel'
import { MeetingViewChatPanel } from './chat-panel'
import type { MeetingViewModel } from '../use-meeting-view-model'

interface MeetingViewBodyProps {
  model: MeetingViewModel
  layout: MeetingViewLayout
}

/** Added to the `list` inset to land halfway to the `fade` one. */
const MEETING_BODY_EXTRA_INSET_PX = 16

// The Get Ready hero's exit when a brief lands: a quick plain fade — the new
// layout then staggers in (Join button first, brief after; see
// meeting-join-button.tsx and the sections wrapper below). Exit faster than
// the enters so the swap reads as one gesture, not two equal halves.
/** Exposed so the header can hold its CTA row out of layout for exactly this long. */
export const GET_READY_EXIT_SECONDS = 0.15

const getReadyVariants: Variants = {
  resting: { opacity: 1 },
  exit: { opacity: 0, transition: { duration: GET_READY_EXIT_SECONDS, ease: 'easeOut' } },
}

// Strong ease-out — the built-in curve is too soft for an entrance to read
// as deliberate.
const EASE_OUT_STRONG = [0.23, 1, 0.32, 1] as const

// Second in the stagger, ~80ms behind the header Join button's entrance
// (meeting-join-button.tsx): mode="wait" already holds this mount until the
// hero's 150ms fade finishes, so only the gap between the two enters is
// encoded here.
const sectionsEnterVariants: Variants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.25, ease: EASE_OUT_STRONG, delay: 0.08 } },
}

/**
 * The Meeting View's scrollable body, tabbed Brief | Transcript | Recap
 * (HIG-6354), or with the `tabs` layout Brief | Notes | Transcript | Recap |
 * Chat. Each tab is its own document: the transcript streams and pins to
 * its live edge while recording, the recap holds the follow-ups over the
 * summary, and the brief is what to read before either exists. Until the
 * meeting is being transcribed there is nothing to tab between, so the bar
 * stays out and the brief is the whole body under the header card (see
 * `BriefPresentation`).
 */
export function MeetingViewBody({ model, layout }: MeetingViewBodyProps) {
  const isLive = model.variant === 'live'
  // Tabs only once the meeting is being transcribed or is finished. Before
  // that there is nothing to tab between, and the brief reads as the body
  // under the header card. Deliberately not `isUpcomingWithoutAudioNote`: that
  // flag latches on the first render carrying a calendar event, so a meeting
  // whose event lands after its start time would take the tabbed body with an
  // empty transcript and an empty recap in it.
  const showTabs = hasMeetingTabs(model)
  const hoursPaywallVisible = useHoursPaywallVisible(model)
  // Between the two named insets: `list` (24px) leaves the last line grazing the
  // footer, `fade` (80px) leaves a dead gap under it. Half the fade span clears
  // the floating controls with the tail of the line still legible through the
  // dissolve. While the meeting-hours paywall docks below this body it provides the
  // footer clearance itself, so the full inset would end the scrolled content
  // on a dead gap above the card.
  const bottomChrome = useViewBodyBottomChrome()
  const footerInset = useViewBodyBottomInset('list') + MEETING_BODY_EXTRA_INSET_PX
  const paddingBottom = hoursPaywallVisible ? MEETING_BODY_EXTRA_INSET_PX : footerInset
  const shouldReduceMotion = useReducedMotion()
  const summary = useMeetingSummary(model)
  const { hasBrief, presentation: briefPresentation } = useMeetingBriefBody(model)
  const canGenerateBrief = useCanGenerateBrief(model)
  // Held while a brief may still land, so a fetch resolving doesn't pop a tab
  // into the bar under the user.
  const hasBriefTab = hasBrief || model.briefLoading || model.briefGenerating || canGenerateBrief
  const isTabbed = layout === 'tabs'
  const { tab: chosenTab, setTab } = useMeetingTab(model, hasBriefTab, { hasChatTab: isTabbed })
  const tabs = listMeetingTabs(layout, hasBriefTab)
  // The redesigned sidebar's meeting is one column, live or saved: captured action items over the transcript
  const transcriptOnly = layout === 'one-column' && showTabs
  // A pick stored under another layout can name a tab this one doesn't show
  const tab = transcriptOnly || !tabs.includes(chosenTab) ? 'transcript' : chosenTab
  const isLiveTranscript = showTabs && isLive && tab === 'transcript'
  // What `Tabs` is actually showing, which is the brief whenever there are no
  // tabs — a stored pick naming another one would otherwise have the panels
  // claim the bar's copy for something off screen.
  const openTab = showTabs ? tab : 'brief'

  // Frozen at mount: `useBodyScroll` only arms its one-shot initial scroll while
  // this is true, so flipping it later (a tab switch) would fire a
  // jump-to-latest they didn't ask for; the switch effect below owns that.
  const [initialAutoScroll] = useState(() => isLiveTranscript)
  const { showScrollButton, scrollToBottom, pinToBottom, onContentChange, stopFollowing, scrollRef } = useBodyScroll({
    initialAutoScroll,
    // Unlike initialAutoScroll this tracks the live state: once the meeting
    // ends the body becomes a document to read, and the summary and follow-ups
    // growing into it must not drag the reader to the bottom.
    followGrowth: isLiveTranscript,
  })

  // useBodyScroll's scrollRef is a callback ref; mirror the node into an
  // object ref for the persistence hook.
  const scrollElRef = useRef<HTMLDivElement | null>(null)
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null)
  const composedScrollRef = useCallback(
    (node: HTMLDivElement | undefined) => {
      const element = node ?? null
      scrollElRef.current = element
      setScrollElement(element)
      scrollRef(node)
    },
    [scrollRef],
  )
  // Off while live: the transcript always opens at its bottom edge, and a
  // restored offset would land the view mid-backlog *and* fight the pin that
  // put it at the bottom (HIG-5978). Ended meetings keep ADR 0001's behaviour —
  // they're documents you read, so back/forward returns where you left off.
  useViewEntryScrollPersistence(scrollElRef, { enabled: !isLive })

  // Jump-to-latest renders in the floating bar's left slot so it sits on the
  // same row as the notepad/audio controls; registered here because the scroll
  // state lives with this body. While the meeting-hours paywall docks below this
  // body, the floating bar sits below the card, where a jump-to-latest would
  // point at the paywall rather than the content it scrolls; the button moves
  // into the body's own bottom corner instead (rendered below).
  const setScrollSlot = useSetFloatingBarScrollSlot()
  useEffect(() => {
    if (hoursPaywallVisible) {
      setScrollSlot(null)
      return
    }
    setScrollSlot({ show: showScrollButton, onClick: scrollToBottom })
    return () => setScrollSlot(null)
  }, [showScrollButton, scrollToBottom, setScrollSlot, hoursPaywallVisible])

  const handleTabChange = useCallback(
    (value: unknown) => {
      if (!isMeetingTab(value)) return
      AmplitudeRenderer.trackEvent('Audio Note Action', { action: 'switch tab', tab: value })
      setTab(value)
    },
    [setTab],
  )

  // One scroll offset is persisted per history entry, and the tabs have
  // wildly different heights, so each switch says where it lands rather than
  // inheriting whatever the browser clamped the old offset to. A document opens
  // at its top; the live transcript opens at its live edge.
  const prevTab = useRef(tab)
  useEffect(() => {
    if (prevTab.current === tab) {
      return
    }
    prevTab.current = tab
    if (isLive && tab === 'transcript') {
      pinToBottom()
    } else {
      scrollElRef.current?.scrollTo({ top: 0 })
    }
  }, [tab, isLive, pinToBottom])

  // Was the last-painted frame the settled Get Ready hero? Read on the render
  // where the presentation flips to gate the sections' staggered entrance —
  // mirroring the hero's exit gate below, so a brief fetch resolving on view
  // open swaps instantly instead of animating.
  const heroWasSettled = useRef(false)
  useEffect(() => {
    heroWasSettled.current = briefPresentation === 'get-ready' && !model.briefLoading
  })

  // Pre-meeting with no brief yet: the hero fills the body on its own — no
  // scroll container, no tabs, nothing else in this state (see
  // BriefPresentation). The AnimatePresence spans both branches so the hero
  // plays its exit when a brief lands; `mode="wait"` sequences hero-out before
  // the body mounts. No exit while the brief fetch is unresolved — a cached
  // brief landing on view open should swap instantly, not float.
  return (
    <AnimatePresence mode="wait" initial={false}>
      {briefPresentation === 'get-ready' ? (
        <motion.div
          key="get-ready"
          className="flex min-h-0 flex-1 flex-col"
          variants={getReadyVariants}
          initial={false}
          animate="resting"
          exit={shouldReduceMotion || model.briefLoading ? undefined : 'exit'}
        >
          <MeetingViewGetReady model={model} />
        </motion.div>
      ) : (
        <motion.div
          key="sections"
          variants={sectionsEnterVariants}
          initial={heroWasSettled.current && !shouldReduceMotion ? 'hidden' : false}
          animate="visible"
          className="relative flex min-h-0 flex-1 flex-col"
        >
          {/* One Tabs root for both layouts: pre-meeting it's pinned to the
              brief with no bar, so the brief keeps flowing straight out of the
              header card. Base UI unmounts inactive panels, which is what keeps
              the tabs isolated — the transcript's virtualizer and the recap's
              queries only exist for the tab on screen. */}
          <Tabs value={openTab} onValueChange={handleTabChange} className="min-h-0 flex-1">
            {showTabs &&
              !transcriptOnly &&
              (isTabbed ? (
                <div className="shrink-0 pb-3">
                  <MeetingViewUnderlinedTabBar tabs={tabs} />
                </div>
              ) : (
                <div className="shrink-0 px-3 pb-3">
                  <MeetingViewTabBar hasBriefTab={hasBriefTab} />
                </div>
              ))}
            {/* The thread scrolls itself, so the Chat tab takes the scroller's place. */}
            {showTabs && isTabbed && (
              <Tabs.Panel value="chat" className="flex min-h-0 flex-1 flex-col">
                <MeetingViewChatPanel model={model} />
              </Tabs.Panel>
            )}
            {/* useBodyScroll owns this scroller's position, and the virtualizer
                swaps estimated row heights for measured ones as lines stream in.
                Browser scroll anchoring fights the pin for scrollTop on every one
                of those corrections. */}
            {tab !== 'chat' && (
              <FadingScrollable
                ref={composedScrollRef}
                fadeSize="3%"
                disableFadeTop
                bottomChrome={bottomChrome}
                className="min-h-0 flex-1 [overflow-anchor:none]"
                style={{ paddingBottom }}
              >
                {/* One wrapper around the panels: useBodyScroll's ResizeObserver
                  watches the scroller's firstElementChild, so it has to be the
                  element whose height tracks the whole document. */}
                <div className="flex flex-col">
                  <FreeGraceHourNote model={model} />
                  <HoursRanOutNote model={model} />
                  <Tabs.Panel value="brief" className="flex flex-col">
                    {/* `none` here means nothing is recorded and there's no brief
                      either, so the panel offers the way to ask for one. */}
                    {showTabs || briefPresentation === 'none' ? (
                      <MeetingViewBriefPanel model={model} isActiveTab={openTab === 'brief'} />
                    ) : (
                      <MeetingViewBriefBody model={model} isActiveTab={openTab === 'brief'} />
                    )}
                  </Tabs.Panel>
                  {showTabs && (
                    <>
                      <Tabs.Panel value="transcript" className="flex flex-col">
                        <MeetingViewTranscriptPanel
                          model={model}
                          showsCapturedActions={layout === 'one-column'}
                          isActiveTab={openTab === 'transcript'}
                          onContentChange={onContentChange}
                          pinToBottom={pinToBottom}
                          stopFollowing={stopFollowing}
                          scrollElement={scrollElement}
                        />
                      </Tabs.Panel>
                      <Tabs.Panel value="recap" className="flex flex-col">
                        <MeetingViewRecapPanel
                          model={model}
                          summary={summary}
                          followUpAppearance={isTabbed ? 'todo' : 'row'}
                          isActiveTab={openTab === 'recap'}
                        />
                      </Tabs.Panel>
                      {isTabbed && (
                        <Tabs.Panel value="notepad" className="flex flex-col">
                          <MeetingViewNotepadPanel model={model} />
                        </Tabs.Panel>
                      )}
                    </>
                  )}
                </div>
              </FadingScrollable>
            )}
          </Tabs>
          {/* The body's own jump-to-latest, hosted here only while the meeting-hours
              paywall claims the space below (see the slot registration above).
              Mirrors the floating bar host: a pointer-events-none anchor the
              button opts back into. */}
          {hoursPaywallVisible && (
            <div className="pointer-events-none absolute bottom-2 left-3 z-[1]">
              <ScrollButton show={showScrollButton} onClick={scrollToBottom} />
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  )
}
